import { useState, Fragment } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Clock, DollarSign, CheckCircle, XCircle, Eye, Building, RefreshCw } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { getShifts, GroupedShifts, recalculateShiftTotalsAdmin, getCompletedRentalsForOperator } from "@/integrations/supabase/services";
import { calculateShiftOutstandingBalance, formatDateFull } from "@/lib/utils";
import CloseShiftModal from "@/components/shifts/CloseShiftModal";
import PayShiftModal from "@/components/shifts/PayShiftModal";
import ShiftDetailsModal from "@/components/shifts/ShiftDetailsModal";
import { RentalWithRelations, ShiftWithRelations, UserProfile } from "@/types/models";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Shifts = () => {
  const [selectedShift, setSelectedShift] = useState<ShiftWithRelations | null>(null);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isRecalculateAlertOpen, setIsRecalculateAlertOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile, isLoading: authLoading } = useAuth();

  // Fetch shifts
  const { data: shiftsData, isLoading: shiftsLoading } = useQuery({
    queryKey: ['shifts', profile?.id, profile?.role],
    queryFn: () => getShifts(profile as UserProfile | null),
    enabled: !!profile && !authLoading,
  });

  const shouldFetchCompletedRentals = (isCloseModalOpen || isDetailsModalOpen) && !!selectedShift;

  const { data: completedRentals = [], isLoading: completedRentalsLoading } = useQuery({
    queryKey: ['completed-rentals', selectedShift?.id],
    queryFn: () => selectedShift ? getCompletedRentalsForOperator(selectedShift.operator_id, selectedShift.start_time) : Promise.resolve([]),
    enabled: shouldFetchCompletedRentals,
  });

  const isLoading = authLoading || shiftsLoading;

  const recalculateMutation = useMutation({
    mutationFn: (shiftId: string) => recalculateShiftTotalsAdmin(shiftId),
    onSuccess: () => {
      toast({
        title: "Jornada Recalculada",
        description: "Los totales de la jornada han sido recalculados exitosamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error en Recálculo",
        description: error.message || "No se pudo recalcular la jornada.",
      });
    },
    onSettled: () => {
      setIsRecalculateAlertOpen(false);
      setSelectedShift(null);
    },
  });

  const handleCloseShift = (shift: ShiftWithRelations) => {
    setSelectedShift(shift);
    setIsCloseModalOpen(true);
  };

  const handlePayShift = (shift: ShiftWithRelations) => {
    setSelectedShift(shift);
    setIsPayModalOpen(true);
  };

  const handleViewShift = (shift: ShiftWithRelations) => {
    setSelectedShift(shift);
    setIsDetailsModalOpen(true);
  };

  const handleRecalculateClick = (shift: ShiftWithRelations) => {
    setSelectedShift(shift);
    setIsRecalculateAlertOpen(true);
  };

  const confirmRecalculate = () => {
    if (selectedShift) {
      recalculateMutation.mutate(selectedShift.id);
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['shifts'] });
    toast({
      title: "Operación exitosa",
      description: "La jornada ha sido actualizada correctamente",
    });
  };

  const getShiftStatus = (shift: ShiftWithRelations) => {
    switch (shift.status) {
      case 'open':
        return <Badge variant="secondary">Activa</Badge>;
      case 'closed':
        return <Badge variant="destructive">Cerrada</Badge>;
      case 'in_review':
        return <Badge variant="warning">Revisión</Badge>;
      case 'paid':
        return <Badge variant="default">Pagada</Badge>;
      case 'partially_paid':
        return <Badge variant="pending">Abonado</Badge>;
      default:
        return <Badge>Desconocido</Badge>;
    }
  };

  const canCloseShift = (shift: ShiftWithRelations) => {
    return shift.status === 'open';
  };

  const canPayShift = (shift: ShiftWithRelations) => {
    return shift.status === 'closed' || shift.status === 'in_review' || shift.status === 'partially_paid' && calculateShiftOutstandingBalance(shift) < 0;
  };

  // Helper functions defined inside the Shifts component scope
  const renderShiftRow = (shift: ShiftWithRelations, isGlobalAdminView: boolean) => {
    return (
      <TableRow key={shift.id} className={getShiftRowClass(shift)}>
        <TableCell className="font-medium">
          {shift.operator?.full_name || 'N/A'}
          {isGlobalAdminView && shift.operator?.branch?.name && (
            <span className="block text-xs text-muted-foreground">{shift.operator?.branch?.name}</span>
          )}
        </TableCell>
        <TableCell>{formatDateFull(shift.start_time)}</TableCell>
        <TableCell>
          {shift.end_time ? formatDateFull(shift.end_time) : '-'}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <DollarSign size={16} className="text-green-600" />
            {(shift.total_collected || 0).toFixed(2)}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <DollarSign size={16} className={calculateShiftOutstandingBalance(shift) >= 0 ? "text-green-600" : "text-red-600"} />
            <span className={calculateShiftOutstandingBalance(shift) >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
              {calculateShiftOutstandingBalance(shift).toFixed(2)}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Clock size={16} className="text-blue-600" />
            {shift.total_rentals}
          </div>
        </TableCell>
        <TableCell>{getShiftStatus(shift)}</TableCell>
        <TableCell>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewShift(shift)}
              className="flex items-center gap-1"
              title="Ver detalles"
            >
              <Eye size={16} />
            </Button>
            {profile?.role === 'super_admin' && shift.end_time && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRecalculateClick(shift)}
                className="flex items-center gap-1"
                title="Recalcular Totales"
              >
                <RefreshCw size={16} />
              </Button>
            )}
            {canCloseShift(shift) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCloseShift(shift)}
                className="flex items-center gap-1"
                title="Cerrar jornada"
              >
                <XCircle size={16} className="text-red-600" />
              </Button>
            )}
            {canPayShift(shift) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePayShift(shift)}
                className="flex items-center gap-1"
                title="Abonar jornada"
              >
                <CheckCircle size={16} />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const getShiftRowClass = (shift: ShiftWithRelations): string => {
    switch (shift.status) {
      case 'open':
        return "bg-yellow-50 hover:bg-yellow-100"; // Active
      case 'closed':
        return "bg-red-50 hover:bg-red-100"; // Pending payment
      case 'in_review':
        return "bg-blue-50 hover:bg-blue-100"; // In review
      case 'partially_paid':
        return "bg-orange-50 hover:bg-orange-100"; // Partially paid
      default:
        return ""; // Default for paid or other states
    }
  };

  const RenderShiftsTableBody = () => {
    if (!shiftsData || !Array.isArray(shiftsData)) {
      if (profile?.role === 'operator' || !shiftsData) {
        return (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-4">
              No hay jornadas registradas o error al cargar.
            </TableCell>
          </TableRow>
        );
      }
      return null;
    }

    const operatorShifts = shiftsData as ShiftWithRelations[];
    if (operatorShifts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-4">
            No tienes jornadas registradas.
          </TableCell>
        </TableRow>
      );
    }
    return <>{operatorShifts.map(shift => renderShiftRow(shift, false))}</>;
  };

  const renderAdminView = () => {
    const groupedShiftsData = shiftsData as GroupedShifts[];
    if (!groupedShiftsData || groupedShiftsData.length === 0) {
      return (
         <div className="bg-white shadow-md rounded-lg overflow-hidden p-4 text-center">
            No hay jornadas para mostrar.
        </div>
      );
    }

    return (
      <Accordion type="multiple" className="w-full space-y-2">
        {groupedShiftsData.map((group, index) => {
          const totals = group.shifts.reduce(
            (acc, shift) => {
              const outstandingBalance = calculateShiftOutstandingBalance(shift);
              acc.amountTotalCollected += shift.total_collected || 0;
              acc.amountTotalOperatorCollected += shift.operator_amount || 0;
              acc.amountTotalAdminCollected += shift.amount_admin || 0; 
              acc.pendingPaymentCollected += outstandingBalance;
              acc.closedAndPaidCollected += shift.amount_admin - outstandingBalance || 0;
              return acc;
            },
            {
              amountTotalCollected: 0,
              amountTotalOperatorCollected: 0,
              amountTotalAdminCollected: 0,
              closedAndPaidCollected: 0,
              pendingPaymentCollected: 0,
            }
          );

          return (
            <AccordionItem value={group.branchId || `branch-${index}`} key={group.branchId || `branch-key-${index}`} className="bg-white shadow-md rounded-lg">
              <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 rounded-t-lg">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center w-full">
                  <div className="flex items-center gap-2 mb-2 md:mb-0">
                    <Building size={20} className="text-primary" />
                    <span className="font-semibold text-lg">{group.branchName}</span>
                    <Badge variant="outline" className="ml-2">{group.shifts.length} Jornadas</Badge>
                  </div>
                  <div className="text-xs text-gray-600 pr-4">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Total:</span>
                        <Badge variant="outline">${totals.amountTotalCollected.toFixed(2)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">T. Operador:</span>
                        <Badge variant="secondary">${totals.amountTotalOperatorCollected.toFixed(2)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">T. Admin:</span>
                        <Badge variant="outline">${totals.amountTotalAdminCollected.toFixed(2)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Pagado:</span>
                        <Badge variant="default">${totals.closedAndPaidCollected.toFixed(2)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Pendiente:</span>
                        <Badge variant="destructive">${totals.pendingPaymentCollected.toFixed(2)}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pt-0 pb-2">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operador</TableHead>
                        <TableHead>Inicio</TableHead>
                        <TableHead>Fin</TableHead>
                        <TableHead>Total Recaudado</TableHead>
                        <TableHead>Saldo Pendiente</TableHead>
                        <TableHead>Alquileres</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-center">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.shifts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-4">
                            No hay jornadas para esta sucursal.
                          </TableCell>
                        </TableRow>
                      ) : (
                        (() => {
                          let closedAndPaidPending = 0;
                          let closedAndPaidCollected = 0;
                          let closedAndPaidPendingCollected = 0;

                          const shiftElements = group.shifts.map(shift => {
                            const outstandingBalance = calculateShiftOutstandingBalance(shift);
                            let currentStatusType = "";

                            if ((shift.end_time && !shift.fully_paid) || shift.status === 'in_review') {
                              currentStatusType = "PENDING";
                              closedAndPaidPending += outstandingBalance || 0;
                              closedAndPaidPendingCollected += shift.total_collected || 0;
                            } else if (!shift.end_time) {
                              currentStatusType = "ACTIVE";
                            } else if (shift.end_time && shift.fully_paid && shift.status === "paid") {
                              currentStatusType = "PAID";
                              closedAndPaidCollected += shift.total_collected || 0;
                            }
                            return { ...shift, currentStatusType };
                          });

                          return shiftElements.map((shift, index) => {
                            const prevShift = index > 0 ? shiftElements[index - 1] : null;
                            const showSeparator = !prevShift || shift.currentStatusType !== prevShift.currentStatusType;

                            return (
                              <Fragment key={shift.id}>
                                {showSeparator && (
                                  <TableRow className="bg-gray-100 hover:bg-gray-300">
                                    <TableCell colSpan={3} className="py-2 px-4 text-sm font-semibold text-gray-600">
                                      {shift.currentStatusType === "PENDING" && "Jornadas con Pagos Pendientes"}
                                      {shift.currentStatusType === "ACTIVE" && "Jornadas Activas"}
                                      {shift.currentStatusType === "PAID" && "Jornadas Cerradas y Pagadas"}
                                    </TableCell>
                                    <TableCell className="py-2 px-4 text-sm font-semibold text-gray-600">
                                      {shift.currentStatusType === "PAID" && <Badge variant="outline">${closedAndPaidCollected.toFixed(2)}</Badge>}
                                      {shift.currentStatusType === "PENDING" && <Badge variant="outline">${closedAndPaidPendingCollected.toFixed(2)}</Badge>}
                                    </TableCell>
                                    <TableCell colSpan={4} className="py-2 px-4 text-sm font-semibold text-gray-600">
                                      {shift.currentStatusType === "PENDING" && <Badge variant="outline">${closedAndPaidPending.toFixed(2)}</Badge>}
                                    </TableCell>
                                  </TableRow>
                                )}
                                {renderShiftRow(shift, false)}
                              </Fragment>
                            );
                          });
                        })()
                      )}
                    </TableBody>
                  </Table>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Jornadas</h1>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="ml-3 text-lg">Cargando jornadas...</p>
          </div>
        )}

        {!isLoading && (profile?.role === 'super_admin' || profile?.role === 'branch_admin') && renderAdminView()}

        {!isLoading && profile?.role === 'operator' && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operador</TableHead>
                  <TableHead>Inicio</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Total Recaudado</TableHead>
                  <TableHead>Saldo Pendiente</TableHead>
                  <TableHead>Alquileres</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4">
                      Cargando jornadas...
                    </TableCell>
                  </TableRow>
                ) : (
                  <RenderShiftsTableBody />
                )}
              </TableBody>
            </Table>
          </div>
        )}
        </div>

      <CloseShiftModal
        shift={selectedShift}
        isOpen={isCloseModalOpen}
        onClose={() => {
          setIsCloseModalOpen(false);
          setSelectedShift(null);
        }}
        onSuccess={handleSuccess}
        completedRentals={completedRentals}
        rentalsLoading={completedRentalsLoading}
      />
      <PayShiftModal
        shift={selectedShift}
        isOpen={isPayModalOpen}
        onClose={() => {
          setIsPayModalOpen(false);
          setSelectedShift(null);
        }}
        onSuccess={handleSuccess}
      />
      <ShiftDetailsModal
        shift={selectedShift}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedShift(null);
        }}
        completedRentals={completedRentals}
        rentalsLoading={completedRentalsLoading}
      />
      <AlertDialog open={isRecalculateAlertOpen} onOpenChange={setIsRecalculateAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción recalculará los totales de la jornada basándose en los alquileres registrados. Los valores actuales se sobrescribirán. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedShift(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRecalculate} disabled={recalculateMutation.isPending}>
              {recalculateMutation.isPending ? "Recalculando..." : "Continuar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
};

export default Shifts;
